/**
 * Steps 1 to 3: setup (brand, what is promoted, market, campaign type), the goal, and the insights: the brand and its
 * competitors in a line each, the market in a line, what customers say (praise and doubts), the trends, and three
 * strategies built on them to pick from, with the client's own idea under them. A pick lights up what it stands on;
 * every later step reads it. Everything here was read or written before the step opens.
 */
import { Heart, Loader2, MessageCircle, Play, Target, TrendingUp } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { H1 } from '@/cowork/campaignOrder/v5/ui';
import { OUTCOME_ICON } from '@/cowork/campaignOrder/v5/parts';
import { useActiveBrand } from '@/cowork/useActiveBrand';
import type { Insights, Prepared } from './api';
import { BEATLY_GRADIENT, Field, InYours, Logo, OnBadge, Section, Select, StrategyCard, StrategySkeleton, card, litCls, selectable } from './kit';
import { shapeKey, type PlannerState } from './state';

export interface StepProps { p: Prepared; s: PlannerState; set: (patch: Partial<PlannerState>) => void }

// ── 1 Setup ───────────────────────────────────────────────────────────────

export function SetupStep({ p, s, set }: StepProps) {
  const { brands, selectBrand } = useActiveBrand();
  const products = [{ id: '__brand', name: p.brand.name, line: 'The whole brand', imageUrl: p.brand.iconUrl || p.brand.logoUrl, brand: true }, ...p.products.slice(0, 11).map((x) => ({ id: x.id, name: x.name, line: x.category || x.description, imageUrl: x.imageUrl, brand: false }))];
  const known = products.some((x) => x.name === s.product);
  return (
    <>
      <H1 title="Set up your campaign" sub="What you promote, where, and how. Everything else starts from your Brand OS." />
      <Section title="Brand">
        {brands.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => {
              const on = b.brandId === p.brand.id;
              return (
                <button key={b.brandId} type="button" aria-pressed={on} onClick={() => !on && selectBrand(b.brandId)} className={cn(selectable(on), 'flex-row items-center gap-3 py-2.5 pl-2.5 pr-10')}>
                  <Logo src={on ? p.brand.iconUrl || p.brand.logoUrl : (b as { logoUrl?: string | null }).logoUrl} name={b.brandName} size={36} />
                  <span className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{b.brandName}</span>
                  <OnBadge on={on} />
                </button>
              );
            })}
          </div>
        ) : (
          <div className={cn(card, 'flex items-center gap-4 p-4')}>
            <Logo src={p.brand.iconUrl || p.brand.logoUrl} name={p.brand.name} size={48} />
            <div className="min-w-0"><div className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{p.brand.name}</div><p className="line-clamp-1 text-[13px] text-[#667085]">{p.brand.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</p></div>
          </div>
        )}
      </Section>

      <Section title="What you promote" note="From your Brand OS">
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((x) => {
            const on = s.product === x.name;
            return (
              <button key={x.id} type="button" onClick={() => set({ product: x.name })} aria-pressed={on} className={cn(selectable(on), 'flex-row items-center gap-3 p-3 pr-10', x.brand && !on && 'bg-[#f8f5ff]')}>
                <Logo src={x.imageUrl} name={x.name} size={44} contain={x.brand} />
                <span className="min-w-0"><span className="block truncate text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{x.name}</span><span className={cn('block truncate text-[12px]', x.brand ? 'text-[#be43ff]' : 'text-[#667085]')}>{x.line}</span></span>
                <OnBadge on={on} />
              </button>
            );
          })}
        </div>
        <input key={s.product} aria-label="Something else" defaultValue={known ? '' : s.product} onBlur={(e) => e.target.value.trim() && set({ product: e.target.value.trim() })} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="Or something else: a range, a launch, an event" className="h-11 w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 text-sm text-[#101828] placeholder:text-[#98a2b3] focus:border-[#be43ff] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30" />
      </Section>

      <section className="grid gap-3 sm:grid-cols-2">
        <Field label="Target country"><Select label="Target country" value={s.market} options={p.options.countries.map((c) => ({ id: c.id, label: c.name }))} onChange={(v) => set({ market: v, audienceCountry: v, ...(v !== s.market ? { strategy: null } : {}) })} /></Field>
        <Field label="Language of the content"><Select label="Language" value={s.language} options={p.options.languages.map((c) => ({ id: c.id, label: c.name || c.id }))} onChange={(v) => set({ language: v })} /></Field>
      </section>

      <Section title="Campaign type">
        <div className="grid gap-3 sm:grid-cols-2">
          <TypeCard on={s.type === 'influencer'} onClick={() => set({ type: 'influencer', objective: s.objective === 'Content production (UGC)' ? null : s.objective, blocksEdited: false })}
            title="Influencer campaign" line="Creators post about you on their own channels, and the best posts run as ads." art={<InfluencerArt />} />
          <TypeCard on={s.type === 'ugc'} onClick={() => set({ type: 'ugc', objective: 'Content production (UGC)', blocksEdited: false })}
            title="User generated content" line="Creators make videos for your ads and your own channels. Raw files included." art={<UgcArt />} />
        </div>
      </Section>
    </>
  );
}

function TypeCard({ on, onClick, title, line, art }: { on: boolean; onClick: () => void; title: string; line: string; art: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} className={cn(selectable(on), 'overflow-hidden')}>
      <div className="relative h-40 w-full overflow-hidden" style={{ background: BEATLY_GRADIENT }}>{art}</div>
      <div className="flex flex-col gap-1 p-4"><span className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{title}</span><span className="text-[13px] text-[#667085]">{line}</span></div>
      <OnBadge on={on} />
    </button>
  );
}
/** Three phones with creator posts: the channel is theirs. */
function InfluencerArt() {
  const phone = (x: string, rot: number, z: number, tone: string) => (
    <span className="absolute bottom-[-18px] flex h-[150px] w-[84px] flex-col justify-between rounded-[16px] border-[3px] border-[#101828] p-1.5" style={{ left: x, transform: `rotate(${rot}deg)`, zIndex: z, background: tone }}>
      <span className="flex items-center gap-1"><span className="h-3.5 w-3.5 rounded-full bg-white/90" /><span className="h-1.5 w-8 rounded-full bg-white/70" /></span>
      <span className="flex flex-col items-end gap-1.5 pb-5 pr-0.5 text-white"><Heart className="h-3 w-3 fill-white" /><MessageCircle className="h-3 w-3" /></span>
    </span>
  );
  return (
    <>
      <span className="absolute left-5 top-4 rounded-full bg-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] text-white" style={{ fontWeight: 600 }}>On their channels</span>
      {phone('34%', -9, 1, 'linear-gradient(180deg,#F6C9A8,#C9764F)')}{phone('47%', 0, 3, 'linear-gradient(180deg,#A9F600,#4F8A3B)')}{phone('60%', 9, 2, 'linear-gradient(180deg,#9FB9F2,#3F5BA9)')}
    </>
  );
}
/** Video frames in their formats: the content is yours. */
function UgcArt() {
  const frame = (w: number, h: number, x: string, y: number, label: string) => (
    <span className="absolute grid place-items-center rounded-lg border-2 border-white/80 bg-white/10 backdrop-blur-[1px]" style={{ width: w, height: h, left: x, top: y }}>
      <span className="grid h-6 w-6 place-items-center rounded-full bg-white text-[#2E024C]"><Play className="ml-0.5 h-3 w-3 fill-[#2E024C]" /></span>
      <span className="absolute bottom-1 left-1.5 text-[9px] text-white" style={{ fontWeight: 600 }}>{label}</span>
    </span>
  );
  return (
    <>
      <span className="absolute left-5 top-4 rounded-full bg-[#A9F600] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] text-[#14200A]" style={{ fontWeight: 600 }}>Raw files included</span>
      {frame(62, 110, '30%', 38, '9:16')}{frame(80, 100, '47%', 48, '4:5')}{frame(84, 84, '66%', 58, '1:1')}
    </>
  );
}

// ── 2 Goal ────────────────────────────────────────────────────────────────

export function GoalStep({ p, s, set }: StepProps) {
  const list = p.options.objectives.filter((o) => (s.type === 'ugc' ? o.id === 'Content production (UGC)' : o.id !== 'Content production (UGC)'));
  return (
    <>
      <H1 title="What should it achieve?" sub="One goal. It shapes the media plan and the ideas Lykke prepares while you go on." />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((o) => {
          const Icon = OUTCOME_ICON[o.id]; const on = s.objective === o.id;
          return (
            <button key={o.id} type="button" aria-pressed={on} onClick={() => set({ objective: o.id, blocksEdited: false })} className={cn(selectable(on), 'gap-3 p-5')}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl text-white" style={{ background: on ? BEATLY_GRADIENT : 'linear-gradient(135deg,#f3e8ff,#fae8ff)' }}>{Icon && <Icon className={cn('h-6 w-6', !on && 'text-[#be43ff]')} aria-hidden />}</span>
              <span className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{o.label}</span>
              <OnBadge on={on} />
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── 3 Insights ────────────────────────────────────────────────────────────

export function InsightsStep({ p, s, set, insights, writing }: StepProps & { insights: Insights | null; writing: boolean }) {
  const market = p.options.countries.find((c) => c.id === s.market)?.name || s.market;
  const brief = p.briefs[s.market];
  const lineOf = (name: string, fallback: string) => Object.entries(insights?.competitors || {}).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1] || fallback;
  const trends = insights?.trends.length ? insights.trends : (brief?.trends || []).slice(0, 4).map((t) => ({ id: undefined, label: t.split(/[,.:;]/)[0].split(' ').slice(0, 4).join(' '), why: '' }));
  const strategies = insights?.v === 2 ? insights.strategies || [] : [];
  const cited = new Set(s.strategy ? s.strategy.refs.map((r) => r.id) : []);
  const lit = (id?: string) => !!id && cited.has(id);
  const marketLit = lit(`m:${s.market.toLowerCase()}`);
  const pick = (st: (typeof strategies)[number]) => set({ strategy: s.strategy?.id === st.id && !s.strategy.matched ? null : { ...st, market: s.market, insightsAt: insights?.at ?? null } });
  const shaped = !!s.shapedFrom && s.shapedFrom === shapeKey(s) && s.builtOn?.from === 'ahead';
  return (
    <>
      <H1 title="What matters for this campaign" sub={`Your brand, the field and ${market} at a glance. Then pick the strategy your campaign builds on.`} />

      <div className="grid gap-3 lg:grid-cols-[1fr_1.3fr]">
        <div className={cn(card, 'flex flex-col gap-3 p-5')}>
          <div className="flex items-center gap-3">
            <Logo src={p.brand.iconUrl || p.brand.logoUrl} name={p.brand.name} size={44} />
            <div className="min-w-0"><div className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{p.brand.name}</div><div className="text-[13px] text-[#be43ff]" style={{ fontWeight: 600 }}>{insights?.brandLine || p.brand.usp.split(/[.;]/)[0] || p.brand.industry}</div></div>
          </div>
          {(insights?.marketLine || brief?.snapshot) && (
            <div className={cn('rounded-xl p-3 transition-shadow', marketLit ? 'bg-[#fdfaff] shadow-[0_0_0_1.5px_#be43ff]' : 'bg-[#f9fafb]')}>
              <div className="flex items-center justify-between gap-2"><span className="text-[10px] uppercase tracking-[0.08em] text-[#667085]" style={{ fontWeight: 600 }}>{market} right now</span>{marketLit && <InYours />}</div>
              <p className="mt-1 text-[13px] text-[#344054]">{insights?.marketLine || brief!.snapshot.split(/(?<=\.)\s/)[0]}</p></div>
          )}
          {p.history?.last && <p className="text-[12px] text-[#667085]">Last campaign with Beatly: <b className="text-[#101828]">{p.history.last.name}</b></p>}
        </div>
        <div className={cn(card, 'flex flex-col gap-3 p-5')}>
          <div className="flex items-center justify-between"><span className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>The field</span><span className="text-[12px] text-[#667085]">{p.competitors.length} competitors</span></div>
          {p.competitors.length ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {p.competitors.slice(0, 6).map((c) => (
                <div key={c.name} className={cn('flex items-center gap-2.5 rounded-xl border p-2.5 transition-shadow', lit(c.id) ? litCls : 'border-gray-100')}>
                  <Logo src={c.logo} name={c.name} size={36} />
                  <span className="min-w-0"><span className="block truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{c.name}</span><span className="line-clamp-2 block text-[12px] leading-snug text-[#667085]">{lineOf(c.name, c.line)}</span>{lit(c.id) && <InYours />}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[13px] text-[#667085]">No competitors in your Brand OS yet.</p>}
        </div>
      </div>

      {p.quotes.length > 0 && (
        <Section icon={MessageCircle} title="What customers say" note="From your reviews and comments">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {p.quotes.map((q, i) => (
              <div key={q.id || q.text} className={cn(card, 'flex gap-3 p-3.5 transition-shadow', lit(q.id) && litCls)}>
                <Logo name={q.theme || `Customer ${i + 1}`} size={34} round />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2"><span className="text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>{q.theme || 'Customer'}</span>
                    {q.tone === 'doubt' ? <span className="rounded-full bg-[#fffaeb] px-2 py-0.5 text-[11px] text-[#b54708]" style={{ fontWeight: 600 }}>Doubt</span> : <Heart className="h-3.5 w-3.5 fill-[#f04438] text-[#f04438]" aria-label="Praise" />}</div>
                  <p className="mt-0.5 text-[13px] leading-snug text-[#344054]">“{q.text}”</p>
                  {lit(q.id) && <span className="mt-1.5 inline-flex"><InYours /></span>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {trends.length > 0 && (
        <Section icon={TrendingUp} title="Trends to ride" note={`In ${market}`}>
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {trends.map((t) => (
              <div key={t.label} className={cn(card, 'flex flex-col gap-1.5 p-4 transition-shadow', lit(t.id) && litCls)}>
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#A9F600]/30 text-[#3B5B00]"><TrendingUp className="h-4 w-4" aria-hidden /></span>
                <span className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{t.label}</span>
                {t.why && <span className="text-[12px] text-[#667085]">{t.why}</span>}
                {lit(t.id) && <InYours />}
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="relative overflow-hidden rounded-2xl p-5 text-white" style={{ background: BEATLY_GRADIENT }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-white/80" style={{ fontWeight: 600 }}><Target className="h-4 w-4" aria-hidden />What this means for your campaign</div>
          {strategies.length > 0 && <span className="text-[12.5px] text-white/85">Pick one to build on</span>}
        </div>
        {strategies.length ? (
          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            {strategies.map((st) => <StrategyCard key={st.id} st={st} p={p} mode="pick" state={s.strategy?.id === st.id ? (s.strategy.matched ? 'matched' : 'on') : 'off'} onPick={() => pick(st)} />)}
          </div>
        ) : !writing && insights?.conclusion.length ? (
          <ul className="mt-3 flex flex-col gap-2">{insights.conclusion.map((c) => <li key={c} className="flex gap-2.5 text-[14px]"><span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-[#A9F600]" aria-hidden />{c}</li>)}</ul>
        ) : (
          <>
            <div className="mt-4 grid gap-3 lg:grid-cols-3"><StrategySkeleton /><StrategySkeleton /><StrategySkeleton /></div>
            <p className="mt-3 text-[13px] text-white/80"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Lykke is reading it. It lands here in a moment; you can go on.</p>
          </>
        )}
        <label className="mt-5 flex flex-col gap-1.5">
          <span className="text-[12.5px] text-white" style={{ fontWeight: 600 }}>Your idea <span className="ml-1 text-white/65" style={{ fontWeight: 500 }}>Optional</span></span>
          <textarea key={s.ideaText} defaultValue={s.ideaText} onBlur={(e) => e.target.value !== s.ideaText && set({ ideaText: e.target.value })} rows={2}
            placeholder="A line or two: your idea, or your own angle if none of these fits."
            className="w-full resize-y rounded-xl border-0 bg-white px-4 py-2.5 text-[14px] leading-relaxed text-[#101828] placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#A9F600]" />
          <span className="text-[12px] text-white/75">{shaped ? 'Shaped. It waits for you on Concept.' : 'Lykke shapes it into your concept while you pick creators.'}</span>
        </label>
      </div>
    </>
  );
}
